import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonBaseProps {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Inverts the neutral variants for use on the dark cobalt panels. */
  onDark?: boolean;
  /** Stretches the control to the width of its container. */
  block?: boolean;
  className?: string;
}

type ButtonAsButton = ButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> & { href?: undefined };

type ButtonAsLink = ButtonBaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps> & { href: string };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

/**
 * Port of components/forms/Button.jsx. Hover, press and focus states moved from
 * inline handlers to CSS so they also fire for keyboard and touch input.
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  onDark = false,
  block = false,
  className,
  ...rest
}: ButtonProps) {
  const classes = [
    styles.button,
    styles[variant],
    styles[size],
    onDark ? styles.onDark : null,
    block ? styles.block : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (rest.href !== undefined) {
    return (
      <a className={classes} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {children}
      </a>
    );
  }

  const { href: _href, ...buttonProps } = rest;
  return (
    <button type="button" className={classes} {...buttonProps}>
      {children}
    </button>
  );
}
